import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, resolveComponent as _resolveComponent, withKeys as _withKeys } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between flex-wrap gap-4" }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "flex items-center flex-wrap gap-4" }
const _hoisted_4 = { class: "bg-gray-100 min-w-5 flex items-center justify-center rounded-[5px] px-1.5 py-px font-semibold" }
const _hoisted_5 = { class: "text-red-500" }
const _hoisted_6 = {
  for: "unread",
  class: "cursor-pointer select-none text-normal font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
}
const _hoisted_7 = {
  key: 0,
  class: "flex items-center justify-center px-4 py-16"
}
const _hoisted_8 = {
  key: "content",
  class: "max-w-full space-y-5 mt-12"
}
const _hoisted_9 = { class: "flex items-center justify-between space-x-4" }
const _hoisted_10 = { class: "flex items-center space-x-3" }
const _hoisted_11 = { class: "size-6" }
const _hoisted_12 = { class: "font-bold text-lg" }
const _hoisted_13 = { class: "text-sm text-gray-500" }
const _hoisted_14 = { class: "mt-5 space-y-5" }
const _hoisted_15 = {
  key: 2,
  class: "flex items-center justify-center px-4 py-16"
}
const _hoisted_16 = { class: "text-gray-500 text-center" }

import { format, parseISO, subMonths } from 'date-fns';
import { computed, ref, nextTick, provide, watch } from 'vue';
import { ChevronDown } from 'lucide-vue-next';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import { Barn, IconSource, ProductionGroup, ProductionGroupBarnResponseEntry } from '@/types';
import { store } from '@/plugins/store';
import { useBarnProductionGroups } from '@/plugins/store/actions/queries/production-groups';
import { useSectionById } from './useSectionById';
import BarnSection from './BarnSection.vue';
import PeriodRangeDropdown from '@/components/ui/PeriodRangeDropdown.vue';
import DeleteGroupModal from './DeleteGroupModal.vue';
import CreateGroupModal from './CreateGroupModal.vue';
import BackfillWizardModal from './BackfillWizardModal.vue';
import ManageProductionGroupModal, { type ManageProductionGroupTarget } from '../production-groups/ManageProductionGroupModal.vue';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { buttonVariants, Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const GROUP_OVERVIEW_PERIOD_STORAGE_KEY = 'group-overview-period';


export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const sitesAreLoaded = computed(() => store.state.sitesAreLoaded);
const barnIds = computed(() => store.getters.getSelectedSiteIdsFilteredByPigType);

// This page's own date range - deliberately NOT the global header PeriodPicker/store value shared
// by ~60 other pages (its 3-month default was too narrow to get an overview of groups, and widening
// it there would've widened every other page's default too). Sticky per-browser via its own
// localStorage key; defaults to the last 12 months so most barns' group history is visible at once.
function readStoredPeriod(): string {
    try {
        const stored = window.localStorage.getItem(GROUP_OVERVIEW_PERIOD_STORAGE_KEY);
        if (stored && stored.includes(' - ')) {
            return stored;
        }
    } catch {
        // localStorage unavailable (private browsing etc.) - fall through to default.
    }
    const today = new Date();
    return `${subMonths(today, 12).toISOString()} - ${today.toISOString()}`;
}

const periodDateString = ref(readStoredPeriod());

watch(periodDateString, (newValue) => {
    try {
        window.localStorage.setItem(GROUP_OVERVIEW_PERIOD_STORAGE_KEY, newValue);
    } catch {
        // ignore write failures
    }
});

// Same `from=yyyy-MM-dd&to=yyyy-MM-dd` shape the global picker's getSelectedPeriodByDate getter
// produces - GetProductionGroups takes this as an opaque query-string fragment either way.
const selectedPeriod = computed(() => {
    const [from, to] = periodDateString.value.split(' - ');
    return `from=${format(parseISO(from), 'yyyy-MM-dd')}&to=${format(parseISO(to), 'yyyy-MM-dd')}`;
});

const isQueryEnabled = computed(() => barnIds.value.length > 0);

const { data: productionGroupBarns, isLoading: isProductionGroupsLoading } = useBarnProductionGroups({ barnIds, selectedPeriod }, { enabled: isQueryEnabled });
const isLoading = computed(() => isProductionGroupsLoading.value || !sitesAreLoaded.value);

const showOnlyFavorites = ref(false);
// One search box per Farm card (see template) rather than one global box - each filters only its own
// Farm's Barns/groups, so typing in one card can't hide rows in another.
const farmSearchQueries = ref<Record<number, string>>({});

// Each Barn's manual All-groups/By-section choice, lifted up here (rather than owned locally in
// BarnSection) because a Weaners/Slaughter pigs toggle can drop a Barn out of `filteredData` entirely
// while the other species is selected and bring it back - unmounting/remounting that BarnSection
// instance, which would otherwise discard a local ref's choice. Keyed by barn_id so it survives that.
const barnViewModeOverrides = ref<Record<number, 'all' | 'bySection'>>({});

// Every Farm card is always rendered (see filteredData below), so on an account with many Farms the
// one you want can sit well below the fold - this is a one-shot "scroll to" action, not a filter, so
// it doesn't narrow what's on the page the way the per-card search or the global header picker do.
const farmCardRefs = new Map<number, HTMLElement>();

function setFarmCardRef(farmId: number, el: Element | { $el?: Element } | null) {
    const node = el && '$el' in el ? el.$el : el;

    if (node instanceof HTMLElement) {
        farmCardRefs.set(farmId, node);
    } else {
        farmCardRefs.delete(farmId);
    }
}

const jumpToFarmId = ref<string | undefined>(undefined);

function handleJumpToFarm(value: string) {
    // Commit the pick first - Select is controlled off this ref, and the underlying Radix combobox
    // latches its displayed value from this prop actually changing. Resetting straight to `undefined`
    // without ever writing `value` in between is a no-op from its perspective (undefined -> undefined),
    // so the trigger would keep showing the picked farm's name indefinitely instead of reverting.
    jumpToFarmId.value = value;
    farmCardRefs.get(Number(value))?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    nextTick(() => {
        jumpToFarmId.value = undefined;
    });
}

const data = computed(() => {
    const farms: {
        farm_id: number;
        farm_name: string;
        barns: (ProductionGroupBarnResponseEntry & { farm_id: number; barn_name: string })[];
        groupCount: number;
    }[] = [];

    const barns =
        productionGroupBarns.value
            ?.map((b) => {
                const barn = store.state.barns.find((stateBarn) => stateBarn.barn_id === b.barn_id);

                if (!barn) {
                    return null;
                }

                const productionGroups = b.production_groups
                    ?.filter((sp) => (showOnlyFavorites.value ? sp.is_favorite : true))
                    .map((sp) => ({
                        ...sp,
                        // Parsed to Date for `$d()` display, though ProductionGroup types them `string` - the
                        // convention BarnSection documents (it casts back at each use), so cast to match its prop.
                        start: (sp.start ? parseISO(sp.start as string) : null) as unknown as string,
                        end: (sp.end ? parseISO(sp.end as string) : null) as unknown as string,
                    }));

                return {
                    ...b,
                    farm_id: barn.farm_id,
                    production_groups: productionGroups,
                    barn_name: barn.name,
                };
            })
            .filter((b): b is NonNullable<typeof b> => b !== null) || [];

    for (const farm of store.state.farms) {
        const farmBarns = barns.filter((b) => b.farm_id === farm.farm_id && store.state.sections?.some((s) => s.barn_id === b.barn_id));

        if (farmBarns.length) {
            farms.push({
                farm_id: farm.farm_id,
                farm_name: farm.name,
                barns: farmBarns,
                groupCount: farmBarns.map((b) => b.production_groups.length).reduce((a, b) => a + b, 0),
            });
        }
    }

    return farms;
});

const sectionById = useSectionById();

// Matches a group's own name or the name of the Section it's located in - a farmer scanning the
// Location column and typing what they see should get a hit, not just a name-only match.
function matchesSearch(group: { name: string; location_id: number }, query: string): boolean {
    const locationName = sectionById.value.get(group.location_id)?.name ?? '';
    return group.name.toLowerCase().includes(query) || locationName.toLowerCase().includes(query);
}

// Filters each Farm's own Barns down to matching groups, but never drops a Barn or Farm card
// outright - a zero-match search renders as each Barn's table showing its own empty state (same
// as the page-level favorites filter), instead of the whole card silently vanishing.
const filteredData = computed(() =>
    data.value.map((farm) => {
        const query = (farmSearchQueries.value[farm.farm_id] ?? '').trim().toLowerCase();

        if (!query) {
            return farm;
        }

        return {
            ...farm,
            barns: farm.barns.map((barn) => ({
                ...barn,
                production_groups: barn.production_groups.filter((sp) => matchesSearch(sp, query)),
            })),
        };
    })
);

const showDeleteModal = ref(false);
const selectedGroupIds = ref<number[]>([]);

async function handleDelete(groupId: number) {
    selectedGroupIds.value = [groupId];
    await nextTick();
    showDeleteModal.value = true;
}

async function handleBulkDelete() {
    selectedGroupIds.value = getSelectedProductionGroups().map((sp) => sp.id);
    await nextTick();
    showDeleteModal.value = true;
}

const showCreateModal = ref(false);
const barnToCreateFrom = ref<Barn | null>(null);

async function handleCreate(barnId: number) {
    const barn = store.state.barns.find((b) => b.barn_id === barnId) || null;

    if (!barn) {
        return;
    }

    barnToCreateFrom.value = barn;
    await nextTick();
    showCreateModal.value = true;
}

// Resolves the Farm/Barn/Section names the shared manage modal displays as context, from whichever
// ids the caller already has (an existing group's, or the Barn + Section just chosen for a new one).
function resolveManageTarget(barnId: number, sectionId: number, productionGroupId: number | null): ManageProductionGroupTarget | null {
    const section = sectionById.value.get(sectionId);
    const barn = store.state.barns.find((b) => b.barn_id === barnId);

    if (!section || !barn) {
        return null;
    }

    const farm = store.state.farms.find((f) => f.farm_id === barn.farm_id);

    return {
        id: sectionId,
        sectionName: section.name,
        farmName: farm?.name ?? '',
        barnName: barn.name,
        barnId,
        productionGroupId,
    };
}

const isManageModalOpen = ref(false);
const manageTarget = ref<ManageProductionGroupTarget | null>(null);
const manageProductionGroup = ref<ProductionGroup | null>(null);

async function handleSectionChosen(sectionId: number) {
    const barnId = barnToCreateFrom.value?.barn_id;
    const target = barnId != null ? resolveManageTarget(barnId, sectionId, null) : null;

    if (!target) {
        return;
    }

    manageTarget.value = target;
    manageProductionGroup.value = null;
    await nextTick();
    isManageModalOpen.value = true;
}

async function handleDeleteFromManageModal(groupId: number) {
    isManageModalOpen.value = false;
    await handleDelete(groupId);
}

const showBackfillModal = ref(false);
const barnToBackfillFrom = ref<Barn | null>(null);
// Set when Backfill is launched from a Section's own expanded panel (Groups overview's By-section
// view) rather than the Barn-level button - pre-fills the wizard's Section picker so the farmer
// doesn't have to re-pick the Section they were already looking at.
const backfillInitialSectionId = ref<number | null>(null);

async function handleBackfill(barnId: number, sectionId: number | null = null) {
    const barn = store.state.barns.find((b) => b.barn_id === barnId) || null;

    if (!barn) {
        return;
    }

    barnToBackfillFrom.value = barn;
    backfillInitialSectionId.value = sectionId;
    await nextTick();
    showBackfillModal.value = true;
}

async function handleEdit(group: ProductionGroup) {
    // `group` comes from the table row, whose dates were parsed to `Date` objects for `$d()` display
    // (see `data` above) - the raw wire object (string dates) is what the manage modal needs, so it's
    // looked up fresh from the query result rather than trusted from the row.
    const rawGroup = productionGroupBarns.value?.flatMap((b) => b.production_groups ?? []).find((g) => g.id === group.id) ?? group;
    const target = resolveManageTarget(rawGroup.parent_location_id, rawGroup.location_id, rawGroup.id);

    if (!target) {
        return;
    }

    manageTarget.value = target;
    manageProductionGroup.value = rawGroup;
    await nextTick();
    isManageModalOpen.value = true;
}

const barnSelections = ref<Record<number, Record<number, boolean>>>({});

watch(
    () => productionGroupBarns.value,
    (barns) => {
        barnSelections.value = {};

        if (barns) {
            for (const barn of barns) {
                if (!barnSelections.value[barn.barn_id]) {
                    barnSelections.value[barn.barn_id] = {};
                }
            }
        }
    }
);

function handleSelectionChange(barnId: number, selections: Record<number, boolean>) {
    barnSelections.value[barnId] = selections;
}

provide('barnSelections', {
    selections: barnSelections,
    updateSelections: handleSelectionChange,
});

const selectedProductionGroupIds = computed(() =>
    Object.entries(barnSelections.value)
        .filter(([barnId]) => productionGroupBarns.value?.some((b) => b.barn_id.toString() === barnId))
        // eslint-disable-next-line no-unused-vars
        .flatMap(([barnId, selections]) => Object.keys(selections))
);

function getSelectedProductionGroups() {
    return productionGroupBarns.value?.flatMap((b) => b.production_groups?.filter((sp) => selectedProductionGroupIds.value.includes(sp.id.toString())) || []) || [];
}

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createBlock(DashboardContentLayout, {
    heading: _ctx.$t('views.monitoring.groupOverview.headings.title'),
    "has-pig-type-picker": "",
    "slim-pig-type-picker": ""
  }, {
    "header-top-right": _withCtx(() => [
      _createVNode(PeriodRangeDropdown, {
        value: periodDateString.value,
        "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((periodDateString).value = $event))
      }, null, 8, ["value"])
    ]),
    default: _withCtx(() => [
      _cache[9] || (_cache[9] = _createElementVNode("div", {
        class: "border-t border-t-gray-200 mt-5 mb-6",
        "aria-hidden": "true"
      }, null, -1)),
      _createElementVNode("div", _hoisted_1, [
        (filteredData.value.length > 1)
          ? (_openBlock(), _createBlock(_unref(Select), {
              key: 0,
              "model-value": jumpToFarmId.value,
              "onUpdate:modelValue": handleJumpToFarm
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(SelectTrigger), { class: "w-56" }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(SelectValue), {
                      placeholder: _ctx.$t('views.monitoring.groupOverview.fields.jumpToFarm.placeholder')
                    }, null, 8, ["placeholder"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(SelectContent), null, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredData.value, (farm) => {
                      return (_openBlock(), _createBlock(_unref(SelectItem), {
                        key: farm.farm_id,
                        value: String(farm.farm_id)
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(farm.farm_name), 1)
                        ]),
                        _: 2
                      }, 1032, ["value"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model-value"]))
          : (_openBlock(), _createElementBlock("div", _hoisted_2)),
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_unref(DropdownMenu), { modal: false }, {
            default: _withCtx(() => [
              _createVNode(_unref(DropdownMenuTrigger), { "as-child": "" }, {
                default: _withCtx(() => [
                  _createVNode(_unref(Button), {
                    variant: "outline",
                    class: _normalizeClass(["transition-[opacity,background-color]", { 'visible opacity-100': selectedProductionGroupIds.value.length > 0, 'invisible opacity-0': selectedProductionGroupIds.value.length === 0 }])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.buttons.manageSelected')) + " ", 1),
                      _createElementVNode("span", _hoisted_4, _toDisplayString(selectedProductionGroupIds.value.length), 1),
                      _createVNode(_unref(ChevronDown), {
                        class: "size-4",
                        "aria-hidden": "true"
                      })
                    ]),
                    _: 1
                  }, 8, ["class"])
                ]),
                _: 1
              }),
              _createVNode(_unref(DropdownMenuContent), {
                class: "min-w-[var(--radix-dropdown-menu-trigger-width)]",
                align: "end"
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(DropdownMenuItem), {
                    class: "cursor-pointer",
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (barnSelections.value = {}))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('views.monitoring.groupOverview.buttons.unselectAll')), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(DropdownMenuItem), {
                    class: "cursor-pointer",
                    onClick: handleBulkDelete
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.buttons.delete')), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createElementVNode("div", {
            class: _normalizeClass(["cursor-pointer", _unref(cn)(_unref(buttonVariants)({ variant: 'outline' }))]),
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (showOnlyFavorites.value = !showOnlyFavorites.value), ["self"]))
          }, [
            _createVNode(_unref(Checkbox), {
              id: "unread",
              checked: showOnlyFavorites.value,
              "onUpdate:checked": _cache[2] || (_cache[2] = ($event: any) => ((showOnlyFavorites).value = $event))
            }, null, 8, ["checked"]),
            _createElementVNode("label", _hoisted_6, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.fields.showOnlyFavorites')), 1)
          ], 2)
        ])
      ]),
      _createVNode(FadeTransition, { mode: "out-in" }, {
        default: _withCtx(() => [
          (isLoading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createVNode(Spinner, { class: "h-10 w-10" })
              ]))
            : (filteredData.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredData.value, (farm) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: farm.farm_id,
                      ref_for: true,
                      ref: (el) => setFarmCardRef(farm.farm_id, el),
                      class: "border border-gray-300 rounded-lg px-4 py-5 scroll-mt-24"
                    }, [
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("div", _hoisted_11, [
                            _createVNode(_component_Icon, {
                              class: "h-full w-full object-contain",
                              src: _unref(IconSource).FarmsPrimary
                            }, null, 8, ["src"])
                          ]),
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_12, _toDisplayString(farm.farm_name), 1),
                            _createElementVNode("div", _hoisted_13, _toDisplayString(farm.barns.length) + " " + _toDisplayString(farm.barns.length === 1 ? _ctx.$t('views.monitoring.groupOverview.texts.barn') : _ctx.$t('views.monitoring.groupOverview.texts.barns')) + ", " + _toDisplayString(farm.groupCount) + " " + _toDisplayString(farm.groupCount === 1 ? _ctx.$t('views.monitoring.groupOverview.texts.group') : _ctx.$t('views.monitoring.groupOverview.texts.groups')), 1)
                          ])
                        ]),
                        _createVNode(_unref(SearchInput), {
                          "model-value": farmSearchQueries.value[farm.farm_id] ?? '',
                          "aria-label": _ctx.$t('views.monitoring.groupOverview.fields.search.label'),
                          placeholder: _ctx.$t('views.monitoring.groupOverview.fields.search.placeholder'),
                          class: "w-64",
                          "onUpdate:modelValue": ($event: any) => (farmSearchQueries.value[farm.farm_id] = $event),
                          onKeyup: _withKeys(($event: any) => (farmSearchQueries.value[farm.farm_id] = ''), ["esc"])
                        }, null, 8, ["model-value", "aria-label", "placeholder", "onUpdate:modelValue", "onKeyup"])
                      ]),
                      _createElementVNode("div", _hoisted_14, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(farm.barns, (barn) => {
                          return (_openBlock(), _createBlock(BarnSection, {
                            key: barn.barn_id,
                            barn: barn,
                            "search-active": !!(farmSearchQueries.value[farm.farm_id] ?? '').trim(),
                            "manual-view-mode": barnViewModeOverrides.value[barn.barn_id],
                            onCreate: handleCreate,
                            onEdit: handleEdit,
                            onBackfill: handleBackfill,
                            "onUpdate:manualViewMode": ($event: any) => (barnViewModeOverrides.value[barn.barn_id] = $event)
                          }, null, 8, ["barn", "search-active", "manual-view-mode", "onUpdate:manualViewMode"]))
                        }), 128))
                      ])
                    ]))
                  }), 128))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createElementVNode("div", _hoisted_16, _toDisplayString(_ctx.$t('views.monitoring.groupOverview.texts.noData')), 1)
                ]))
        ]),
        _: 1
      }),
      (barnToCreateFrom.value)
        ? (_openBlock(), _createBlock(CreateGroupModal, {
            key: 0,
            modelValue: showCreateModal.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showCreateModal).value = $event)),
            barn: barnToCreateFrom.value,
            onChosen: handleSectionChosen
          }, null, 8, ["modelValue", "barn"]))
        : _createCommentVNode("", true),
      (barnToBackfillFrom.value)
        ? (_openBlock(), _createBlock(BackfillWizardModal, {
            key: 1,
            modelValue: showBackfillModal.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showBackfillModal).value = $event)),
            barn: barnToBackfillFrom.value,
            "initial-section-id": backfillInitialSectionId.value
          }, null, 8, ["modelValue", "barn", "initial-section-id"]))
        : _createCommentVNode("", true),
      _createVNode(ManageProductionGroupModal, {
        modelValue: isManageModalOpen.value,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((isManageModalOpen).value = $event)),
        target: manageTarget.value,
        "production-group": manageProductionGroup.value,
        "allow-delete": "",
        onDelete: handleDeleteFromManageModal
      }, null, 8, ["modelValue", "target", "production-group"]),
      _createVNode(DeleteGroupModal, {
        modelValue: showDeleteModal.value,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((showDeleteModal).value = $event)),
        "group-ids": selectedGroupIds.value,
        onDeleted: _cache[8] || (_cache[8] = ($event: any) => (isManageModalOpen.value = false))
      }, null, 8, ["modelValue", "group-ids"])
    ]),
    _: 1
  }, 8, ["heading"]))
}
}

})